import * as Sentry from '@sentry/angular'

import { environment } from '../../../environments/environment'

/**
 * Reports a reader from a Legal Intelligence or Rechtsorde link who does not
 * end on the article with its full text.
 */

type LinkLoss =
  'not_logged_in' | 'no_seats' | 'article_not_found' | 'no_full_text'

const LINK_PROVIDERS = ['li', 'rechtsorde']

// `no_seats` also covers a login the CMS could not check (website-backend#122).
const LOSS_MESSAGES: Record<LinkLoss, string> = {
  not_logged_in: 'Reader came back from the link login without being logged in',
  no_seats: 'Link login refused for lack of seats, or not checked by the CMS',
  article_not_found: 'Link login landed on an article that does not exist',
  no_full_text: 'Link login landed on an article without its full text',
}

/**
 * The link provider of this visit: `idp_hint` in the address, else the hint
 * stored across the login.
 */
export function readLinkProvider(storedHint: string | null): string | null {
  const hint =
    new URLSearchParams(window.location.search).get('idp_hint') ?? storedHint
  const enabled = environment.keycloak.providers.some(
    provider => provider.idpHint === hint && provider.enabled
  )
  return hint && enabled && LINK_PROVIDERS.includes(hint) ? hint : null
}

export function reportLinkLoss(
  provider: string | null,
  outcome: LinkLoss,
  tags: Record<string, string> = {}
): void {
  if (!provider || !LINK_PROVIDERS.includes(provider)) {
    return
  }
  const error = new Error(LOSS_MESSAGES[outcome])
  error.name = 'LinkLoginLoss'
  // One throw site would otherwise group every outcome as one issue.
  Sentry.captureException(error, {
    tags: { login_provider: provider, link_outcome: outcome, ...tags },
    fingerprint: ['link-login-loss', outcome],
  })
}
